// Client-side feature flags (ТЗ §5.9). Тонкая обёртка над posthog-js
// isFeatureEnabled + onFeatureFlags (polling/realtime). Для критичных
// kill-switch features сервер дополнительно сверяет admin `feature_flags`
// таблицу (ТЗ §5.9 «Sync с admin killswitch») — server-side через
// observability.featureFlags.check.
import { useSyncExternalStore } from "react";
import { getPostHog, isPostHogEnabled } from "./posthog-client";

/**
 * Каталог активных клиентских флагов. Каждый ключ обязан иметь читателя
 * `useFeatureFlag` / `isFeatureEnabled` вне этого модуля (см. тест).
 */
export const FEATURE_FLAG_KEYS = [
	"tutor-voice-inline",
	"tutor-unified-chat-enabled",
	"notes-tiptap3-ux-enabled",
	"sync-recovery-center-enabled",
] as const;

export type FeatureFlagKey = (typeof FEATURE_FLAG_KEYS)[number];

/**
 * Значение флага, когда PostHog не сказал по нему ничего.
 *
 * ПОЧЕМУ дефолт живёт в коде, а не «включим потом в PostHog».
 *
 * PostHog требует ключа сборки и живой сети. Пока дефолтом было жёсткое
 * `false`, любой флаг был выключен ровно в тех условиях, где фича нужнее всего:
 * центр восстановления синхронизации гейтился ОНЛАЙНОВЫМ сервисом, хотя
 * закрывает последствия ОФЛАЙН-сбоя. Журнал отказов при этом продолжал
 * писаться, раздел в `/settings/offline` не рендерился — отказавшие правки
 * копились молча, и пользователь не имел ни одного способа о них узнать.
 * Такая же дыра ждала любой флаг, который понадобится в деградированной среде.
 *
 * Килсвитч при этом сохраняется: `posthog.isFeatureEnabled` различает
 * `undefined` (флаг не заведён / флаги ещё не загружены) и `false` (заведён и
 * ЯВНО выключен). Явное `false` из PostHog по-прежнему гасит фичу; дефолт
 * применяется только там, где сказать было некому.
 *
 * Новый флаг по умолчанию `false` — включать в коде осознанно, для фич,
 * которые обязаны работать без сети.
 */
const FLAG_DEFAULTS: Record<FeatureFlagKey, boolean> = {
	// Inline voice в чате уже в проде; PostHog `false` — kill-switch.
	"tutor-voice-inline": true,
	// Unified smart chat UI chrome: Scenarios/Skills in header, hide mode filter.
	// Generation is always materials (server-side) — flag does NOT restore ModeSwitcher
	// / regular vs materials paths. PostHog `false` only hides the extra header buttons.
	"tutor-unified-chat-enabled": true,
	// Редактор конспектов — базовый UX, не эксперимент.
	"notes-tiptap3-ux-enabled": true,
	// Единственная поверхность, показывающая потерянные офлайн-правки.
	"sync-recovery-center-enabled": true,
};

/** Разовая проверка флага (не реактивная). */
export function isFeatureEnabled(key: FeatureFlagKey): boolean {
	if (!isPostHogEnabled()) return FLAG_DEFAULTS[key];
	try {
		const remote = getPostHog()?.isFeatureEnabled(key);
		return remote === undefined ? FLAG_DEFAULTS[key] : remote === true;
	} catch {
		return FLAG_DEFAULTS[key];
	}
}

/**
 * Реактивный хук флага (ре-рендер при обновлении флагов через
 * posthog.onFeatureFlags). Используется в компонентах для conditional UI.
 */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
	return useSyncExternalStore(
		(onChange) => {
			if (!isPostHogEnabled()) return () => {};
			try {
				// onFeatureFlags возвращает unsubscribe-функцию.
				return getPostHog()?.onFeatureFlags(() => onChange()) ?? (() => {});
			} catch {
				return () => {};
			}
		},
		() => isFeatureEnabled(key),
		// SSR/пререндер: тот же дефолт, иначе первый клиентский рендер после
		// гидрации «мигал» бы выключенной фичей.
		() => FLAG_DEFAULTS[key],
	);
}
